import PropTypes from 'prop-types';
// @mui
import { styled, keyframes } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton } from '@mui/material';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// components
import Iconify from '../../../components/iconify';
import CloseIcon from '@mui/icons-material/Close';
import AccountPopover from './AccountPopover';
import { useNotificationContent } from '../../../contexts/notificationContent';
import { useEffect, useState } from 'react';

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const HEADER_MOBILE = 64;

const HEADER_DESKTOP = 92;

const StyledRoot = styled(AppBar)(({ theme }) => ({
  zIndex: 10,
  ...bgBlur({ color: theme.palette.background.default }),
  boxShadow: 'none',
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${NAV_WIDTH + 1}px)`,
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: HEADER_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: HEADER_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));


const slideInFromTop = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const StyledNotification = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing(4),  // Keeps the notification a little below the top of the viewport
  // left: '50%',             // Centers the notification horizontally
  // transform: 'translateX(-50%)', // Ensures the notification stays centered
  zIndex: 1300,
  backgroundColor: 'rgb(28, 28, 28, 0.9)',
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  width: '80%',
  fontSize: theme.typography.fontSize,
  animation: `${slideInFromTop} 0.5s ease-out`, // Animates from top
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.up('lg')]: {
    minHeight: '70px',
    padding: theme.spacing(3),
  },
}));

// ----------------------------------------------------------------------

Header.propTypes = {
  onOpenNav: PropTypes.func,
};

function formatContentWithLinks(content) {
  // Convert email addresses into <a> tags
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  content = content.replace(emailRegex, '<a style="color: #27c456;" href="mailto:$1">$1</a>');

  // Convert URLs into <a> tags (e.g., https://www.example.com)
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  content = content.replace(urlRegex, '<a style="color: #27c456;" href="$1" target="_blank">$1</a>');

  return content;
}

export default function Header({ onOpenNav }) {
  const [visible, setVisible] = useState(false);
  const { content, interval, fromDate, toDate, enabled } = useNotificationContent()?.notification || {};


  const getNextDisplayTime = () => {
    const storedData = JSON.parse(localStorage.getItem('notificationData')) || {};
    const { closeTime, lastFromDate, lastToDate, lastContent } = storedData;

    if (content !== lastContent || fromDate !== lastFromDate || lastToDate !== toDate) {
      return null;
    }

    if (closeTime) {
      const nextDisplayTime = new Date(closeTime).getTime() + interval * 60 * 60 * 1000;
      return nextDisplayTime;
    }
    return null;
  };

  useEffect(() => {
    if (enabled && interval >= 0) {
      const nextDisplayTime = getNextDisplayTime();
      const now = Date.now();

      const fromDateTimestamp = new Date(fromDate).getTime();
      const toDateTimestamp = new Date(new Date(toDate).getTime() + 24 * 60 * 60 * 1000).getTime();

      if (
        now >= fromDateTimestamp &&
        now <= toDateTimestamp &&
        (!nextDisplayTime || now >= nextDisplayTime)
      ) {
        setVisible(true);
      }
    }
  }, [interval, enabled, fromDate, toDate]);

  // Handle close button click
  const handleClose = () => {
    const closeTime = new Date().toISOString();
    localStorage.setItem(
      'notificationData',
      JSON.stringify({ closeTime, lastFromDate: fromDate, lastToDate: toDate, lastContent: content })
    );
    setVisible(false);
  };

  return (
    <StyledRoot>
      <StyledToolbar>
        <IconButton
          onClick={onOpenNav}
          sx={{
            mr: 1,
            color: 'text.primary',
            display: { lg: 'none' },
          }}
        >
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>

        {
          visible && (
            <StyledNotification>
              <div
                dangerouslySetInnerHTML={{
                  __html: formatContentWithLinks(content.replace(/\n/g, '<br />'))
                  // __html: content?.replace(/\n/g, '<br />'),
                }}
              />
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </StyledNotification>
          )
        }

        <Box sx={{ flexGrow: 1 }} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={{
            xs: 0.5,
            sm: 1,
          }}
        >

          <AccountPopover />
        </Stack>
      </StyledToolbar>
    </StyledRoot>
  );
}
